/* Common Imports */

import { styled } from "@mui/system";
import React from "react";

/* Redux Imports */

import { ObjectId } from "mongodb";
import StateInterface from "../../../../../redux-magic/state-interface";

/* Component Imports */

import { useTheme } from "@mui/material";
import PDF from "../RightContent/DownloadPDF";
import ShareProperty from "../RightContent/ShareProperty";
import AboutDeveloperCard from "./AboutDeveloperCard";
import AboutPropertyCard from "./AboutPropertyCard";
import AmenitiesCard from "./AmenitiesCard";
import ConfigAndAreaCard from "./ConfigAndAreaCard";
import MapCard from "./MapCard";
import OverviewCard from "./OverviewCard";
import PropertyDetailsCard from "./PropertyDetailsCard";
import RegulatoryInfoCard from "./RegulatoryInfoCard";
import VicinityCard from "./VicinityCard";
// import { InView } from "react-intersection-observer";
import { useRouter } from "next/router";
import { BusinessProfileInterface, PropertyInterface, UserInterface } from "../../../../../models";
import { PropertySnackbarInterface } from "../../../../../redux-magic/sub-interfaces/property-snackbar";
import ContactDetailCard from "../RightContent/ContactDetailCard";
import PropertyHighlightsUserView from "./PropertyHighlightsUserView";

/* Styled Components */

const Container = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	gap: "1rem",
	width: "100%",
	overflow: "hidden",
	[theme.breakpoints.down("lg")]: {
		margin: "0rem -2.8rem 0rem 0rem",
	},
	[theme.breakpoints.down("sm")]: {
		margin: "0rem 0rem 0rem 0rem",
	},
}));

const SubContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "stretch",
	gap: "1rem",
	width: "100%",
	height: "100%",
	[theme.breakpoints.down("sm")]: {
		gap: "1rem",
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		alignItems: "center",
		margin: "0rem 0rem 0rem 0rem",
	},
}));

// const CustomTabs = styled(Tabs)(({ theme }) => ({
//   display: "flex",
//   flexDirection: "row",
//   justifyContent: "center",
//   alignItems: "center",
//   boxShadow: " 0px 1px 3px rgba(0, 0, 0, 0.32)",
//   background: theme.palette.background.paper,
//   margin: "0rem 0rem 0rem 0rem",
//   width: "100%",
//   textTransform: "capitalize",
//   minHeight: "2rem",
//   borderRadius: "8px",
//   // flexGrow: 1,
// }));

// const DivContainer = styled("div")(({ theme }) => ({
// 	display: "flex",
// 	flexDirection: "row",
// 	justifyContent: "space-between",
// 	alignItems: "center",
// 	width: "100%",
// 	padding: "1rem",
// 	background: theme.palette.background.paper,
// }));

const MapboxContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	padding: "1.5rem",
	width: "100%",
	boxShadow: theme.palette.mode == "dark" ? "" : "0px 12px 24px -4px rgba(145, 158, 171, 0.12)",
	background: theme.palette.background.paper,
	gap: "1rem",
}));

const Contain = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	width: "100%",
}));

interface LocalPropertyInterface extends Omit<PropertyInterface, "created_by"> {
	created_by: {
		is_business_profile: boolean;
		business_profile_id: string | ObjectId | BusinessProfileInterface;
		user_id: string | ObjectId | UserInterface;
	};
}

const RightContainer = ({
	propertyData,
	dispatch,
	session,
	location,
	profile_context,
	saved_content,
	negativeScroll,
	isEditor,
	loading,
	loading_property_details,
	loading_property_config_and_area,
	loading_about_property,
	loading_overview,
	loading_amenity,
	loading_about_developer,
	loading_about_location,
	loading_vicinity,
	loading_reviews,
	loading_neighbourhood_reviews,
	loading_map,
	section_to_edit,
	data_before_edit,
	property_detailsInnerRef,
	overviewInnerRef,
	aboutPropertyInnerRef,
	amenitiesInnerRef,
	aboutDeveloperInnerRef,
	aboutLocationInnerRef,
	locationCoordinatesInnerRef,
	vicinityInnerRef,
	neighbourhoodReviewsInnerRef,
	reviewsInnerRef,
	// property_details_snackbar,
	property_snackbar,
	propertyDetails_inView,
	singlePropertyOverview_inView,
	aboutSingleProperty_inView,
	singlePropertyAmenities_inView,
	singlePropertyDeveloper_inView,
	singlePropertyAboutLocation_inView,
	singlePropertyReviews_inView,
	singlePropertyLocationCoordinates_inView,
	singlePropertyVicinity_inView,
	is_user_creator,
	logged_user_access,
	logged_user,
	login_flow_global_state,
	screenType,
}: {
	propertyData: StateInterface["single_property_page"];
	dispatch: Function;
	session: any;
	location: StateInterface["location"];
	profile_context: StateInterface["profile_context"];
	saved_content: LocalPropertyInterface["saved_content"];
	negativeScroll: boolean;
	isEditor: boolean;
	loading: boolean;
	loading_property_details: boolean;
	loading_property_config_and_area: boolean;
	loading_about_property: boolean;
	loading_overview: boolean;
	loading_amenity: boolean;
	loading_about_developer: boolean;
	loading_about_location: boolean;
	loading_vicinity: boolean;
	loading_reviews: boolean;
	loading_neighbourhood_reviews: boolean;
	loading_map: boolean;
	section_to_edit: string;
	data_before_edit: any;
	property_detailsInnerRef: any;
	overviewInnerRef: any;
	aboutPropertyInnerRef: any;
	amenitiesInnerRef: any;
	aboutDeveloperInnerRef: any;
	aboutLocationInnerRef: any;
	locationCoordinatesInnerRef: any;
	vicinityInnerRef: any;
	neighbourhoodReviewsInnerRef: any;
	reviewsInnerRef: any;
	property_snackbar: PropertySnackbarInterface;
	propertyDetails_inView: boolean;
	singlePropertyOverview_inView: boolean;
	aboutSingleProperty_inView: boolean;
	singlePropertyAmenities_inView: boolean;
	singlePropertyDeveloper_inView: boolean;
	singlePropertyAboutLocation_inView: boolean;
	singlePropertyReviews_inView: boolean;
	singlePropertyLocationCoordinates_inView: boolean;
	singlePropertyVicinity_inView: boolean;
	is_user_creator: boolean;
	logged_user_access: string;
	logged_user: string | ObjectId;
	login_flow_global_state: StateInterface["login_flow_global_state"];
	screenType: string;
}) => {
	const theme = useTheme();

	const creator_id: ObjectId | string = propertyData.property.created_by
		? propertyData.property.created_by.is_business_profile
			? propertyData.property.created_by.business_profile_id != ""
				? propertyData.property.created_by.business_profile_id
				: propertyData.property.created_by.user_id != ""
					? propertyData.property.created_by.user_id
					: ""
			: propertyData.property.created_by.user_id != ""
				? propertyData.property.created_by.user_id
				: ""
		: "";

	const profile_context_id: ObjectId | string = profile_context
		? profile_context.is_business_profile
			? profile_context.business_profile_id
			: profile_context.user_id
		: "";

	const [value, setValue] = React.useState(0);

	const [isChanged, setIsChanged] = React.useState(false);

	const [locationTrigger, setLocationTrigger] = React.useState(false);

	const updateLocationTrigger = (state: boolean) => setLocationTrigger(state);

	React.useEffect(() => {
		let orderArray: Array<boolean> = [
			propertyDetails_inView,
			singlePropertyOverview_inView,
			aboutSingleProperty_inView,
			singlePropertyAmenities_inView,
			singlePropertyDeveloper_inView,
			singlePropertyAboutLocation_inView,
			singlePropertyVicinity_inView,
			singlePropertyReviews_inView,
		];

		if (propertyData && propertyData.property && propertyData.property.property_type) {
			propertyData.property.property_type.includes("Land (Residential)") ||
			propertyData.property.property_type.includes("Land (Commercial)") ||
			propertyData.property.property_type.includes("Land (Agricultural)") ||
			propertyData.property.property_type.includes("Land (Industrial)")
				? (orderArray = [
						propertyDetails_inView,
						singlePropertyOverview_inView,
						aboutSingleProperty_inView,
						singlePropertyDeveloper_inView,
						singlePropertyAboutLocation_inView,
						singlePropertyVicinity_inView,
						singlePropertyReviews_inView,
					])
				: propertyData.property.property_type.includes("Land (Residential)") ||
					  propertyData.property.property_type.includes("Land (Commercial)") ||
					  propertyData.property.property_type.includes("Land (Agricultural)") ||
					  propertyData.property.property_type.includes("Land (Industrial)") ||
					  propertyData.property.property_type.includes("Co-living/PG") ||
					  propertyData.property.property_type.includes("Builder Floor Apartment") ||
					  propertyData.property.property_type.includes("Independent House")
					? (orderArray = [
							propertyDetails_inView,
							singlePropertyOverview_inView,
							aboutSingleProperty_inView,
							singlePropertyAboutLocation_inView,
							singlePropertyVicinity_inView,
							singlePropertyReviews_inView,
						])
					: (orderArray = [
							propertyDetails_inView,
							singlePropertyOverview_inView,
							aboutSingleProperty_inView,
							singlePropertyAmenities_inView,
							singlePropertyDeveloper_inView,
							singlePropertyAboutLocation_inView,
							singlePropertyVicinity_inView,
							singlePropertyReviews_inView,
						]);
		}

		let currentTab = 0;

		for (let i = 0; i < orderArray.length; i++) {
			if (orderArray[i] === true) {
				currentTab = isChanged ? value : i;
				break;
			}
			currentTab = isChanged ? value : i;
		}
		setValue(currentTab);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		propertyData,
		propertyDetails_inView,
		singlePropertyOverview_inView,
		aboutSingleProperty_inView,
		singlePropertyAmenities_inView,
		singlePropertyDeveloper_inView,
		singlePropertyAboutLocation_inView,
		singlePropertyVicinity_inView,
		singlePropertyReviews_inView,
		isChanged,
	]);
	const router = useRouter();
	const pathname = React.useRef(router).current;

	React.useEffect(() => {
		if (pathname?.query?.property_review_button === "true") {
			document.getElementById("property-reviews")?.scrollIntoView({
				block: "start",
				behavior: "smooth",
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pathname]);

	React.useEffect(() => {
		if (pathname?.query?.neighbourhood_review_button === "true") {
			document.getElementById("neighbourhood-reviews")?.scrollIntoView({
				block: "start",
				behavior: "smooth",
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pathname]);

	//? Show the regulatory info section only for UAE / Abu Dhabi listings
	const isAbuDhabi =
		(propertyData.property.location?.country || "").toLowerCase() === "united arab emirates" &&
		(propertyData.property.location?.state_or_province || "").toLowerCase() === "abu dhabi";

	//? Indian listings show the regulatory section too, but with only a RERA number field.
	const isIndia = (propertyData.property.location?.country || "").toLowerCase() === "india";

	//? Regulatory / RERA info is not shown for rental listings.
	const isRentalProperty = (propertyData.property.transaction_type || "").toLowerCase() === "rent";

	//? When the property's location transitions into Abu Dhabi (e.g. user just edited it), scroll to the regulatory section so they can see/edit those fields.
	const wasAbuDhabiRef = React.useRef(isAbuDhabi);
	React.useEffect(() => {
		if (!wasAbuDhabiRef.current && isAbuDhabi) {
			//? Defer to next tick so the section has mounted before we scroll
			setTimeout(() => {
				document.getElementById("regulatory-info")?.scrollIntoView({
					block: "start",
					behavior: "smooth",
				});
			}, 0);
		}
		wasAbuDhabiRef.current = isAbuDhabi;
	}, [isAbuDhabi]);

	// if (loading) {
	// 	return (
	// 		<Container>
	// 			{/* <DivContainer>
	// 				<Skeleton
	// 					animation="wave"
	// 					variant="text"
	// 					width="5rem"
	// 				/>
	// 				<Skeleton
	// 					animation="wave"
	// 					variant="text"
	// 					width="5rem"
	// 				/>
	// 				<Skeleton
	// 					animation="wave"
	// 					variant="text"
	// 					width="5rem"
	// 				/>
	// 				<Skeleton
	// 					animation="wave"
	// 					variant="text"
	// 					width="5rem"
	// 					sx={{
	// 						[theme.breakpoints.down("sm")]: {
	// 							display: "none",
	// 						},
	// 					}}
	// 				/>
	// 				<Skeleton
	// 					animation="wave"
	// 					variant="text"
	// 					width="5rem"
	// 					sx={{
	// 						[theme.breakpoints.down("sm")]: {
	// 							display: "none",
	// 						},
	// 					}}
	// 				/>
	// 				<Skeleton
	// 					animation="wave"
	// 					variant="text"
	// 					width="5rem"
	// 					sx={{
	// 						[theme.breakpoints.down("sm")]: {
	// 							display: "none",
	// 						},
	// 					}}
	// 				/>
	// 			</DivContainer> */}

	// 			<SubContainer ref={property_detailsInnerRef}>
	// 				{/* Property Details */}

	// 				<PropertyDetailsCard
	// 					propertyData={propertyData.property}
	// 					property_transaction_type={propertyData.property.transaction_type}
	// 					property_category_type={propertyData.property.property_category}
	// 					property_type={propertyData.property.property_type}
	// 					property_subtype={propertyData.property.property_subtype}
	// 					property_min_price={propertyData.property.price.min}
	// 					property_max_price={propertyData.property.price.max}
	// 					property_security_deposit={propertyData.property.security_deposit}
	// 					property_availability={propertyData.property.availability}
	// 					property_possession_date={propertyData.property.possession_date}
	// 					property_tags={propertyData.property.tags}
	// 					property_unit_facing={
	// 						propertyData.property.unit_facing
	// 							? propertyData.property.unit_facing.map(
	// 									(element: string) => element.charAt(0).toUpperCase() + element.slice(1).replace(/_/g, " "),
	// 								)
	// 							: []
	// 					}
	// 					property_land_facing={
	// 						propertyData.property.land_facing
	// 							? propertyData.property.land_facing.map(
	// 									(element: string) => element.charAt(0).toUpperCase() + element.slice(1).replace(/_/g, " "),
	// 								)
	// 							: []
	// 					}
	// 					property_age={propertyData.property.property_age}
	// 					property_created_by={(propertyData.property as LocalPropertyInterface).created_by}
	// 					property_id={propertyData.property._id}
	// 					dispatch={dispatch}
	// 					session={session}
	// 					negativeScroll={negativeScroll}
	// 					isEditor={isEditor}
	// 					profile_context={profile_context}
	// 					section_to_edit={section_to_edit}
	// 					data_before_edit={data_before_edit}
	// 					loading={loading_property_details}
	// 					property_details_snackbar={property_snackbar ? (property_snackbar.property_details as boolean) : false}
	// 				/>

	// 				{/* Config and Area */}

	// 				{propertyData.property.transaction_type === "ofb" ? null : (
	// 					<ConfigAndAreaCard
	// 						propertyData={propertyData.property}
	// 						property_furnishing_status={propertyData.property.furnished}
	// 						property_plot_dimensions={
	// 							propertyData.property.plot_dimensions ? propertyData.property.plot_dimensions : ["30x40"]
	// 						}
	// 						other_plot_dimensions={
	// 							propertyData.property.other_plot_dimensions ? propertyData.property.other_plot_dimensions : ["30x40"]
	// 						}
	// 						property_area={propertyData.property.area}
	// 						property_bhk={propertyData.property.bhk}
	// 						property_bathroom={propertyData.property.bathroom}
	// 						property_balconies={propertyData.property.balconies}
	// 						property_floors={propertyData.property.floors}
	// 						property_floor_no={propertyData.property.floor_no}
	// 						property_no_of_units={propertyData.property.no_of_units}
	// 						property_no_of_seats={propertyData.property.no_of_seats}
	// 						property_created_by={(propertyData.property as LocalPropertyInterface).created_by}
	// 						property_id={propertyData.property._id}
	// 						dispatch={dispatch}
	// 						session={session}
	// 						isEditor={isEditor}
	// 						profile_context={profile_context}
	// 						loading={loading_property_config_and_area}
	// 						section_to_edit={section_to_edit}
	// 						data_before_edit={data_before_edit}
	// 						config_area_snackbar={property_snackbar ? (property_snackbar.property_config as boolean) : false}
	// 					/>
	// 				)}
	// 			</SubContainer>

	// 			{/* Overview */}

	// 			<div
	// 				ref={overviewInnerRef}
	// 				style={{ width: "100%" }}
	// 			>
	// 				<OverviewCard
	// 					overview_data={propertyData.property.description}
	// 					search_metrics={propertyData.property.search_metrics}
	// 					property_id={propertyData.property._id}
	// 					property_created_by={(propertyData.property as LocalPropertyInterface).created_by}
	// 					dispatch={dispatch}
	// 					session={session}
	// 					negativeScroll={negativeScroll}
	// 					isEditor={isEditor}
	// 					profile_context={profile_context}
	// 					loading={loading_overview}
	// 					section_to_edit={section_to_edit}
	// 					data_before_edit={data_before_edit}
	// 					assigned_to={propertyData.property.assigned_to}
	// 					overview_snackbar={property_snackbar ? (property_snackbar.property_overview as boolean) : false}
	// 					slug={propertyData.property.slug}
	// 					aigenerated={propertyData.property.is_overview_ai_generated as boolean}
	// 					manual_overview={propertyData.property.manual_overview as string}
	// 					use_data_for_fine_tuning={propertyData.property.use_data_for_fine_tuning as boolean}
	// 				/>
	// 			</div>

	// 			{/* About Property */}

	// 			{is_user_creator || propertyData.property.transaction_type === "ofb" ? null : (
	// 				<div
	// 					ref={aboutPropertyInnerRef}
	// 					style={{ width: "100%" }}
	// 				>
	// 					<AboutPropertyCard
	// 						property_id={propertyData.property._id}
	// 						about_property={
	// 							propertyData ? (propertyData.property ? propertyData.property.about_property : [""]) : [""]
	// 						}
	// 						property_created_by={(propertyData.property as LocalPropertyInterface).created_by}
	// 						dispatch={dispatch}
	// 						session={session}
	// 						negativeScroll={negativeScroll}
	// 						isEditor={isEditor}
	// 						profile_context={profile_context}
	// 						loading={loading_about_property}
	// 						section_to_edit={section_to_edit}
	// 						data_before_edit={data_before_edit}
	// 						assigned_to={propertyData.property.assigned_to}
	// 						about_property_snackbar={property_snackbar ? (property_snackbar.about_property as boolean) : false}
	// 					/>
	// 				</div>
	// 			)}

	// 			{/* Map */}

	// 			<MapboxContainer>
	// 				<Contain>
	// 					<Skeleton
	// 						animation="wave"
	// 						variant="text"
	// 						width="5rem"
	// 					/>
	// 					<Skeleton
	// 						animation="wave"
	// 						variant="text"
	// 						width="15rem"
	// 					/>
	// 				</Contain>
	// 				<Skeleton
	// 					animation="wave"
	// 					variant="rounded"
	// 					width="100%"
	// 					height="20rem"
	// 				/>
	// 			</MapboxContainer>

	// 			{/* About Location */}

	// 			{is_user_creator ? null : (
	// 				<div
	// 					ref={aboutLocationInnerRef}
	// 					style={{ width: "100%" }}
	// 				>
	// 					<AboutLocationCard
	// 						nearbyProfiles={propertyData.nearbyProperties ?? []}
	// 						about_location={propertyData.property.location.about_location}
	// 						property_created_by={(propertyData.property as LocalPropertyInterface).created_by}
	// 						location={
	// 							propertyData
	// 								? propertyData.property
	// 									? propertyData.property.location
	// 										? propertyData.property.location.title
	// 										: ""
	// 									: ""
	// 								: ""
	// 						}
	// 						property_id={propertyData.property._id}
	// 						dispatch={dispatch}
	// 						session={session}
	// 						negativeScroll={negativeScroll}
	// 						isEditor={isEditor}
	// 						profile_context={profile_context}
	// 						loading={loading_about_location}
	// 						section_to_edit={section_to_edit}
	// 						data_before_edit={data_before_edit}
	// 						assigned_to={propertyData.property.assigned_to}
	// 						location_snackbar={property_snackbar ? (property_snackbar.property_about_location as boolean) : false}
	// 						trigger={locationTrigger}
	// 						updateTrigger={updateLocationTrigger}
	// 					/>
	// 				</div>
	// 			)}

	// 			{/* Amenities */}

	// 			<div
	// 				ref={amenitiesInnerRef}
	// 				style={{ width: "100%" }}
	// 			>
	// 				<AmenitiesCard
	// 					property_amenities={propertyData.property.amenities}
	// 					property_created_by={(propertyData.property as LocalPropertyInterface).created_by}
	// 					property_id={propertyData.property._id}
	// 					dispatch={dispatch}
	// 					session={session}
	// 					negativeScroll={negativeScroll}
	// 					isEditor={isEditor}
	// 					profile_context={profile_context}
	// 					loading={loading_amenity}
	// 					section_to_edit={section_to_edit}
	// 					data_before_edit={data_before_edit}
	// 					assigned_to={propertyData.property.assigned_to}
	// 					amenities_snackbar={property_snackbar ? (property_snackbar.property_amenities as boolean) : false}
	// 				/>
	// 			</div>

	// 			{/* About Developer */}

	// 			<div
	// 				ref={aboutDeveloperInnerRef}
	// 				style={{ width: "100%" }}
	// 			>
	// 				<AboutDeveloperCard
	// 					developers={propertyData.developers ?? []}
	// 					about_developer={propertyData.property.builder}
	// 					property_created_by={(propertyData.property as LocalPropertyInterface).created_by}
	// 					search_metrics={propertyData.property.search_metrics}
	// 					property_id={propertyData.property._id}
	// 					dispatch={dispatch}
	// 					session={session}
	// 					negativeScroll={negativeScroll}
	// 					isEditor={isEditor}
	// 					profile_context={profile_context}
	// 					loading={loading_about_developer}
	// 					section_to_edit={section_to_edit}
	// 					data_before_edit={data_before_edit}
	// 					assigned_to={propertyData.property.assigned_to}
	// 					developer_snackbar={property_snackbar ? (property_snackbar.property_developer as boolean) : false}
	// 				/>
	// 			</div>

	// 			{/* Vicinity */}

	// 			{is_user_creator ? null : (
	// 				<div
	// 					style={{ width: "100%" }}
	// 					ref={vicinityInnerRef}
	// 				>
	// 					<VicinityCard
	// 						location={location.location}
	// 						property_vicinity={propertyData.property.vicinity}
	// 						property_id={propertyData.property._id}
	// 						property_created_by={(propertyData.property as LocalPropertyInterface).created_by}
	// 						dispatch={dispatch}
	// 						session={session}
	// 						isEditor={isEditor}
	// 						profile_context={profile_context}
	// 						loading={loading}
	// 						section_to_edit={section_to_edit}
	// 						data_before_edit={data_before_edit}
	// 						assigned_to={propertyData.property.assigned_to}
	// 						vicinity_snackbar={property_snackbar ? (property_snackbar.property_vicinity as boolean) : false}
	// 						trigger={locationTrigger}
	// 						updateTrigger={updateLocationTrigger}
	// 					/>
	// 				</div>
	// 			)}

	// 			{/* Property Reviews */}

	// 			<div
	// 				ref={reviewsInnerRef}
	// 				style={{ width: "100%" }}
	// 				id="property-reviews"
	// 			>
	// 				<PropertyReviews
	// 					property_id={propertyData.property._id}
	// 					property_slug={propertyData.property.slug}
	// 					location_title={propertyData.property.location.title}
	// 					location_coordinates={propertyData.property.search_metrics.location_coordinates}
	// 					reviews={propertyData.propertyReviews}
	// 					session={session}
	// 					dispatch={dispatch}
	// 					profile_context={profile_context}
	// 					property_created_by={(propertyData.property as LocalPropertyInterface).created_by}
	// 					negativeScroll={negativeScroll}
	// 					loading={loading_reviews}
	// 				/>
	// 			</div>

	// 			{/* <div
	// 				ref={neighbourhoodReviewsInnerRef}
	// 				style={{ width: "100%" }}
	// 				id="neighbourhood-reviews"
	// 			>
	// 				<NeighbourHoodReviews
	// 					property_id={propertyData.property._id}
	// 					location_title={propertyData.property.location.title}
	// 					location_coordinates={propertyData.property.search_metrics.location_coordinates}
	// 					reviews={propertyData.neighbourhoodReviews}
	// 					session={session}
	// 					dispatch={dispatch}
	// 					profile_context={profile_context}
	// 					property_created_by={(propertyData.property as LocalPropertyInterface).created_by}
	// 					loading={loading_neighbourhood_reviews}
	// 					property_slug={propertyData.property.slug}
	// 				/>
	// 			</div> */}
	// 		</Container>
	// 	);
	// }

	// const handleChange = (event: React.SyntheticEvent, newValue: number) => {
	//   setIsChanged(true);
	//   setValue(newValue);

	//   const proertyDetailsSection = document.getElementById("property-details");
	//   // const configAreaSection = document.getElementById("config-area");
	//   const overviewSection = document.getElementById("overview");
	//   const aboutPropertySection = document.getElementById("about-property");
	//   const amenitiesSection = document.getElementById("amenities");
	//   const aboutDeveloperSection = document.getElementById("about-developer");
	//   const aboutLocationSection = document.getElementById("property-location");
	//   const vicinitySection = document.getElementById("vicinity");
	//   const reviewsSection = document.getElementById("reviews");

	//   if (newValue === 0) {
	//     /* scroll to property details section */
	//     if (proertyDetailsSection) {
	//       proertyDetailsSection.scrollIntoView({
	//         behavior: "smooth",
	//       });
	//     }
	//   } else if (newValue === 1) {
	//     /* scroll to overview section */
	//     if (overviewSection) {
	//       overviewSection.scrollIntoView({
	//         behavior: "smooth",
	//       });
	//     }
	//   } else if (newValue === 2) {
	//     /* scroll to about property section */
	//     if (aboutPropertySection) {
	//       aboutPropertySection.scrollIntoView({
	//         behavior: "smooth",
	//       });
	//     }
	//   } else if (newValue === 3) {
	//     /* scroll to amenities section */
	//     if (amenitiesSection) {
	//       amenitiesSection.scrollIntoView({
	//         behavior: "smooth",
	//       });
	//     }
	//   } else if (newValue === 4) {
	//     /* scroll to about developer section */
	//     if (aboutDeveloperSection) {
	//       aboutDeveloperSection.scrollIntoView({
	//         behavior: "smooth",
	//       });
	//     }
	//   } else if (newValue === 5) {
	//     /* scroll to location section */
	//     if (aboutLocationSection) {
	//       aboutLocationSection.scrollIntoView({
	//         behavior: "smooth",
	//       });
	//     }
	//   } else if (newValue === 6) {
	//     /* scroll to vicinity section */
	//     if (vicinitySection) {
	//       vicinitySection.scrollIntoView({
	//         behavior: "smooth",
	//       });
	//     }
	//   } else if (newValue === 7) {
	//     /* scroll to reviews section */
	//     if (reviewsSection) {
	//       const yOffset =
	//         reviewsSection.getBoundingClientRect().top + window.pageYOffset;
	//       window.scrollTo({
	//         top: yOffset,
	//         behavior: "smooth",
	//       });
	//     }
	//   }
	// };

	return (
		<Container>
			{/* <ChatComponent /> */}
			{/* Tabs */}
			{/* <CustomTabs
				value={value}
				onChange={handleChange}
				variant="scrollable"
				scrollButtons="auto"
				allowScrollButtonsMobile
				TabIndicatorProps={{
					style: {
						backgroundColor: theme.palette.primary.main,
					},
				}}
				aria-label="scrollable auto tabs example"
				sx={{
					position: "sticky",
					zIndex: "4",
					top: negativeScroll === true ? "4rem" : "0rem",
				}}
			>
				<Tab label="Property Details" />
				<Tab label="Overview" />
				<Tab label="About Property" />
				{propertyData.property.property_type.includes("Land (Residential)") ||
				propertyData.property.property_type.includes("Land (Commercial)") ||
				propertyData.property.property_type.includes("Land (Agricultural)") ||
				propertyData.property.property_type.includes("Land (Industrial)") ? null : (
					<Tab label="Amenities" />
				)}
				{propertyData.property.property_type.includes("Land (Residential)") ||
				propertyData.property.property_type.includes("Land (Commercial)") ||
				propertyData.property.property_type.includes("Land (Agricultural)") ||
				propertyData.property.property_type.includes("Land (Industrial)") ||
				propertyData.property.property_type.includes("Co-living/PG") ||
				propertyData.property.property_type.includes("Builder Floor Apartment") ||
				propertyData.property.property_type.includes("Independent House") ? null : (
					<Tab label="About Developer" />
				)}
				<Tab label="Location" />
				<Tab label="Vicinity" />
				<Tab label="Reviews" />
			</CustomTabs> */}
			{/* <StickyRootDiv
				id="root"
				sx={{
					position: "sticky",
					zIndex: "4",
					top: negativeScroll === true ? "8rem" : "4rem",
				}}
			>
				divv
			</StickyRootDiv> */}
			{/*
      <InView
        as="div"
        threshold={0.5}
        onChange={(inView, entry) => {
          if (inView === true && entry.isIntersecting === true) {
            // root = document.getElementById("root");
            // setRoot(document.getElementById("root"));
            setValue(0);
          }
        }}
        style={{ width: "100%" }}
      > */}
			{is_user_creator ||
			session?.user?.dbUser?.access_level.includes("super_admin") ||
			session?.user?.dbUser?.access_level.includes("admin") ||
			session?.user?.dbUser?.access_level.includes("property") ||
			propertyData.property.transaction_type === "ofb" ? (
				<SubContainer
					ref={property_detailsInnerRef}
					style={{ width: "100%" }}
				>
					{/* Property Details */}

					<PropertyDetailsCard
						propertyData={propertyData.property}
						property_transaction_type={propertyData.property.transaction_type}
						property_category_type={propertyData.property.property_category}
						property_type={propertyData.property.property_type}
						property_subtype={propertyData.property.property_subtype}
						property_min_price={propertyData.property.price.min}
						property_max_price={propertyData.property.price.max}
						property_currency={propertyData.property.price.currency}
						property_security_deposit={propertyData.property.security_deposit}
						property_availability={propertyData.property.availability}
						property_possession_date={propertyData.property.possession_date}
						property_tags={propertyData.property.tags}
						property_unit_facing={
							propertyData.property.unit_facing
								? propertyData.property.unit_facing.map(
										(element: string) => element.charAt(0).toUpperCase() + element.slice(1).replace(/_/g, " "),
									)
								: []
						}
						property_land_facing={
							propertyData.property.land_facing
								? propertyData.property.land_facing.map(
										(element: string) => element.charAt(0).toUpperCase() + element.slice(1).replace(/_/g, " "),
									)
								: []
						}
						property_age={propertyData.property.property_age}
						property_created_by={(propertyData.property as LocalPropertyInterface).created_by}
						property_id={propertyData.property._id}
						dispatch={dispatch}
						session={session}
						negativeScroll={negativeScroll}
						isEditor={isEditor}
						profile_context={profile_context}
						section_to_edit={section_to_edit}
						data_before_edit={data_before_edit}
						loading={loading_property_details}
						property_details_snackbar={property_snackbar ? (property_snackbar.property_details as boolean) : false}
						screenType={screenType}
					/>

					{/* Config & Area */}

					{propertyData.property.transaction_type === "ofb" ? null : (
						<ConfigAndAreaCard
							propertyData={propertyData.property}
							property_furnishing_status={propertyData.property.furnished}
							property_plot_dimensions={
								propertyData.property.plot_dimensions ? propertyData.property.plot_dimensions : ["30x40"]
							}
							other_plot_dimensions={
								propertyData.property.other_plot_dimensions ? propertyData.property.other_plot_dimensions : ["30x40"]
							}
							property_area={propertyData.property.area}
							property_bhk={propertyData.property.bhk}
							property_bathroom={propertyData.property.bathroom}
							property_balconies={propertyData.property.balconies}
							property_floors={propertyData.property.floors}
							property_floor_no={propertyData.property.floor_no}
							property_no_of_units={propertyData.property.no_of_units}
							property_no_of_seats={propertyData.property.no_of_seats}
							property_created_by={(propertyData.property as LocalPropertyInterface).created_by}
							property_id={propertyData.property._id}
							dispatch={dispatch}
							session={session}
							isEditor={isEditor}
							profile_context={profile_context}
							loading={loading_property_config_and_area}
							section_to_edit={section_to_edit}
							data_before_edit={data_before_edit}
							config_area_snackbar={property_snackbar ? (property_snackbar.property_config as boolean) : false}
							screenType={screenType}
						/>
					)}
				</SubContainer>
			) : (
				<div
					ref={property_detailsInnerRef}
					style={{ width: "100%" }}
				>
					<PropertyHighlightsUserView
						loading={loading_property_details}
						property_id={propertyData.property._id}
						property_title={propertyData.property.title}
						location={propertyData.property.location.title}
						transaction_type={propertyData.property.transaction_type}
						property_category={propertyData.property.property_category}
						property_type={propertyData.property.property_type}
						property_subtype={propertyData.property.property_subtype}
						price={propertyData.property.price}
						price_per_unit={propertyData.property.price_per_unit}
						security_deposit={propertyData.property.security_deposit}
						property_status={propertyData.property.availability}
						possession_date={propertyData.property.possession_date}
						property_age={propertyData.property.property_age}
						unit_facing={propertyData.property.unit_facing}
						land_facing={propertyData.property.land_facing}
						furnishing={propertyData.property.furnished}
						bhk={propertyData.property.bhk}
						bathrooms={propertyData.property.bathroom}
						balconies={propertyData.property.balconies}
						no_of_seats={propertyData.property.no_of_seats}
						no_of_units={propertyData.property.no_of_units}
						no_of_floors={propertyData.property.floors}
						plot_dimensions={propertyData.property.plot_dimensions}
						area={propertyData.property.area}
						profile_context={profile_context}
						dispatch={dispatch}
						session={session}
						saved_content={saved_content}
						property_amenties={propertyData.property.amenities}
						login_flow_global_state={login_flow_global_state}
						screenType={screenType}
					/>
				</div>
			)}
			{/* </InView> */}

			{/* Contact Details */}

			{propertyData.property.published ? (
				profile_context_id === creator_id || isEditor ? null : (
					<ContactDetailCard
						property_created_by={propertyData.property.created_by}
						property_slug={propertyData.property.slug}
						property_id={propertyData.property._id}
						property_title={propertyData.property.title}
						property_show_contact_details={propertyData.property.show_contact_details}
						transaction_type={propertyData.property.transaction_type}
						boost_information={propertyData.property.boost_information}
						profile_context={profile_context}
						dispatch={dispatch}
						session={session}
						loading={loading}
						showOnPhone
						login_flow_global_state={login_flow_global_state}
						screenType={screenType}
					/>
				)
			) : null}

			{/* Overview */}

			{/* <InView
				as="div"
				threshold={0}
				// root={root}
				onChange={(inView, entry) => {
					if (inView === true && entry.isIntersecting === true) {
						setValue(1);
					}
				}}
				style={{ width: "100%" }}
			> */}
			<div
				ref={overviewInnerRef}
				style={{ width: "100%" }}
			>
				<OverviewCard
					overview_data={propertyData.property.description}
					search_metrics={propertyData.property.search_metrics}
					property_id={propertyData.property._id}
					property_created_by={(propertyData.property as LocalPropertyInterface).created_by}
					dispatch={dispatch}
					session={session}
					negativeScroll={negativeScroll}
					isEditor={isEditor}
					profile_context={profile_context}
					loading={loading_overview}
					section_to_edit={section_to_edit}
					data_before_edit={data_before_edit}
					assigned_to={propertyData.property.assigned_to}
					overview_snackbar={property_snackbar ? (property_snackbar.property_overview as boolean) : false}
					slug={propertyData.property.slug}
					aigenerated={propertyData.property.is_overview_ai_generated as boolean}
					manual_overview={propertyData.property.manual_overview as string}
					use_data_for_fine_tuning={propertyData.property.use_data_for_fine_tuning as boolean}
					screenType={screenType}
				/>
			</div>
			{/* </InView> */}

			{/* About Property */}

			{/* <InView
        as="div"
        threshold={0}
        // root={root}
        onChange={(inView, entry) => {
          if (inView === true && entry.isIntersecting === true) {
            setValue(2);
          }
        }}
        style={{ width: "100%" }}
      > */}
			{propertyData.property.transaction_type === "ofb" ||
			(is_user_creator &&
				// propertyData.property.about_property.length < 1 &&
				!session?.user?.dbUser?.access_level.includes("super_admin") &&
				!session?.user?.dbUser?.access_level.includes("admin") &&
				!session?.user?.dbUser?.access_level.includes("property")) ? null : (
				<div
					ref={aboutPropertyInnerRef}
					style={{ width: "100%" }}
				>
					<AboutPropertyCard
						property_id={propertyData.property._id}
						about_property={propertyData.property.about_property}
						property_created_by={(propertyData.property as LocalPropertyInterface).created_by}
						dispatch={dispatch}
						session={session}
						negativeScroll={negativeScroll}
						isEditor={isEditor}
						profile_context={profile_context}
						loading={loading_about_property}
						section_to_edit={section_to_edit}
						data_before_edit={data_before_edit}
						assigned_to={propertyData.property.assigned_to}
						about_property_snackbar={property_snackbar ? (property_snackbar.about_property as boolean) : false}
						screenType={screenType}
					/>
				</div>
			)}
			{/* </InView> */}

			{/* Map View */}

			<div
				style={{ width: "100%" }}
				ref={locationCoordinatesInnerRef}
			>
				<MapCard
					dispatch={dispatch}
					propertyData={propertyData.property}
					property_id={propertyData.property._id}
					property_created_by={(propertyData.property as LocalPropertyInterface).created_by}
					session={session}
					location={location}
					isEditor={isEditor}
					profile_context={profile_context}
					section_to_edit={section_to_edit}
					data_before_edit={data_before_edit}
					loading={loading_map}
					map_snackbar={property_snackbar ? (property_snackbar.property_location_coordinates as boolean) : false}
					trigger={locationTrigger}
					updateTrigger={updateLocationTrigger}
					property_vicinity={propertyData.property.vicinity}
					screenType={screenType}
				/>
			</div>

			{/* About Location */}

			{/* <InView
        as="div"
        threshold={0.5}
        // root={root}
        onChange={(inView, entry) => {
          if (inView === true && entry.isIntersecting === true) {
            setValue(5);
          }
        }}
        style={{ width: "100%" }}
      > */}
			{/* {is_user_creator &&
			// propertyData.property.location.about_location.length < 1 &&
			!session?.user?.dbUser?.access_level.includes("super_admin") &&
			!session?.user?.dbUser?.access_level.includes("admin") &&
			!session?.user?.dbUser?.access_level.includes("property") ? null : (
				<div
					ref={aboutLocationInnerRef}
					style={{ width: "100%" }}
				>
					<AboutLocationCard
						nearbyProfiles={propertyData.nearbyProperties ?? []}
						about_location={propertyData.property.location.about_location}
						property_created_by={(propertyData.property as LocalPropertyInterface).created_by}
						location={
							propertyData
								? propertyData.property
									? propertyData.property.location
										? propertyData.property.location.title
										: ""
									: ""
								: ""
						}
						property_id={propertyData.property._id}
						dispatch={dispatch}
						session={session}
						negativeScroll={negativeScroll}
						isEditor={isEditor}
						profile_context={profile_context}
						loading={loading_about_location}
						section_to_edit={section_to_edit}
						data_before_edit={data_before_edit}
						assigned_to={propertyData.property.assigned_to}
						location_snackbar={property_snackbar ? (property_snackbar.property_about_location as boolean) : false}
						trigger={locationTrigger}
						updateTrigger={updateLocationTrigger}
					/>
				</div>
			)} */}
			{/* </InView> */}

			{/* Amenities */}

			{propertyData.property.property_type.includes("Land (Residential)") ||
			propertyData.property.property_type.includes("Land (Commercial)") ||
			propertyData.property.property_type.includes("Land (Agricultural)") ||
			propertyData.property.property_type.includes("Land (Industrial)") ||
			(propertyData.property.transaction_type === "ofb" &&
				(propertyData.property.property_category === "joint development" ||
					propertyData.property.property_category === "joint venture")) ||
			is_user_creator ||
			session?.user?.dbUser?.access_level.includes("super_admin") ||
			(session?.user?.dbUser?.access_level.includes("admin") &&
				session?.user?.dbUser?.access_level.includes("property")) ? (
				<div
					ref={amenitiesInnerRef}
					style={{ width: "100%" }}
				>
					<AmenitiesCard
						property_amenities={propertyData.property.amenities}
						property_created_by={(propertyData.property as LocalPropertyInterface).created_by}
						property_id={propertyData.property._id}
						dispatch={dispatch}
						session={session}
						negativeScroll={negativeScroll}
						isEditor={isEditor}
						profile_context={profile_context}
						loading={loading_amenity}
						section_to_edit={section_to_edit}
						data_before_edit={data_before_edit}
						assigned_to={propertyData.property.assigned_to}
						amenities_snackbar={property_snackbar ? (property_snackbar.property_amenities as boolean) : false}
						screenType={screenType}
					/>
				</div>
			) : null}

			{/* About Developer */}

			{propertyData.property.property_type.includes("Land (Residential)") ||
			propertyData.property.property_type.includes("Land (Commercial)") ||
			propertyData.property.property_type.includes("Land (Agricultural)") ||
			propertyData.property.property_type.includes("Land (Industrial)") ||
			propertyData.property.property_type.includes("Co-living/PG") ||
			propertyData.property.property_type.includes("Builder Floor Apartment") ||
			propertyData.property.property_type.includes("Independent House") ||
			propertyData.property.property_type.includes("Mining") ||
			(propertyData.property.transaction_type === "ofb" &&
				(propertyData.property.property_category === "joint development" ||
					propertyData.property.property_category === "joint venture")) ||
			(propertyData.property.builder.name === "" &&
				!is_user_creator &&
				!session?.user?.dbUser?.access_level.includes("super_admin") &&
				!session?.user?.dbUser?.access_level.includes("admin") &&
				!session?.user?.dbUser?.access_level.includes("property")) ? null : (
				<div
					ref={aboutDeveloperInnerRef}
					style={{ width: "100%" }}
				>
					<AboutDeveloperCard
						developers={propertyData.developers ?? []}
						about_developer={propertyData.property.builder}
						property_created_by={(propertyData.property as LocalPropertyInterface).created_by}
						search_metrics={propertyData.property.search_metrics}
						property_id={propertyData.property._id}
						dispatch={dispatch}
						session={session}
						negativeScroll={negativeScroll}
						isEditor={isEditor}
						profile_context={profile_context}
						loading={loading_about_developer}
						section_to_edit={section_to_edit}
						data_before_edit={data_before_edit}
						assigned_to={propertyData.property.assigned_to}
						developer_snackbar={property_snackbar ? (property_snackbar.property_developer as boolean) : false}
						screenType={screenType}
					/>
				</div>
			)}

			{/* Regulatory Information — UAE (Abu Dhabi) shows permit/broker; India shows RERA only */}

			{(isAbuDhabi || isIndia) && !isRentalProperty ? (
				<div style={{ width: "100%" }}>
					<RegulatoryInfoCard
						router={router}
						dispatch={dispatch}
						session={session}
						profile_context={profile_context}
						property_id={propertyData.property._id}
						property_rera_number={propertyData.property.rera_number}
						location_country={propertyData.property.location?.country}
						property_regulatory_permit={propertyData.property.regulatory_permit}
						property_broker_license={propertyData.property.broker_license}
						property_created_by={(propertyData.property as LocalPropertyInterface).created_by}
						is_user_creator={is_user_creator}
						logged_user_access={logged_user_access}
						logged_user={logged_user}
						assigned_to={propertyData.property.assigned_to}
						isEditor={isEditor}
						regulatory_info_snackbar={
							property_snackbar ? (property_snackbar.property_regulatory_info as boolean) : false
						}
					/>
				</div>
			) : null}

			{/* Vicinity */}

			{/* {propertyData.property.transaction_type === "ofb" || */}
			{/* // (is_user_creator &&
			// 	!session?.user?.dbUser?.access_level.includes("super_admin") &&
			// 	!session?.user?.dbUser?.access_level.includes("admin") &&
			// 	!session?.user?.dbUser?.access_level.includes("property")) ? null : (
				// <div */}
			{/* // 	style={{ width: "100%" }}
				// 	ref={vicinityInnerRef}
				// >
				// 	<VicinityCard
				// 		location={location.location}
				// 		property_vicinity={propertyData.property.vicinity}
				// 		property_id={propertyData.property._id}
				// 		property_created_by={(propertyData.property as LocalPropertyInterface).created_by}
				// 		dispatch={dispatch}
				// 		session={session}
				// 		isEditor={isEditor}
			// 			profile_context={profile_context}
			// 			loading={loading_vicinity}
			// 			section_to_edit={section_to_edit}
			// 			data_before_edit={data_before_edit}
			// 			assigned_to={propertyData.property.assigned_to}
			// 			vicinity_snackbar={property_snackbar ? (property_snackbar.property_vicinity as boolean) : false}
			// 			trigger={locationTrigger}
			// 			updateTrigger={updateLocationTrigger}
			// 		/>
			// 	</div>
			// )} */}
			{session?.user?.dbUser?.access_level.includes("super_admin") ||
			(session?.user?.dbUser?.access_level.includes("admin") &&
				session?.user?.dbUser?.access_level.includes("property")) ||
			propertyData.property.transaction_type === "ofb" ? (
				<div
					style={{ width: "100%" }}
					ref={vicinityInnerRef}
				>
					<VicinityCard
						location={location.location}
						property_vicinity={propertyData.property.vicinity}
						property_id={propertyData.property._id}
						property_created_by={(propertyData.property as LocalPropertyInterface).created_by}
						dispatch={dispatch}
						session={session}
						isEditor={isEditor}
						profile_context={profile_context}
						loading={loading_vicinity}
						section_to_edit={section_to_edit}
						data_before_edit={data_before_edit}
						assigned_to={propertyData.property.assigned_to}
						vicinity_snackbar={property_snackbar ? (property_snackbar.property_vicinity as boolean) : false}
						trigger={locationTrigger}
						updateTrigger={updateLocationTrigger}
						screenType={screenType}
					/>
				</div>
			) : null}

			{/* Download PDF */}

			{propertyData.property && propertyData.property.published ? (
				<PDF
					published={propertyData.property.published}
					loading={loading}
					showOnPhone
					profile_context={profile_context}
					screenType={screenType}
				/>
			) : null}

			{/* Share Property */}

			{propertyData.property && propertyData.property.published ? (
				<ShareProperty
					url={process.env.PRODUCTION_URL + "properties/" + propertyData.property.slug}
					loading={loading}
					showOnPhone
					user_id={session ? (session.user.dbUser ? (session.user.dbUser._id ? session.user.dbUser._id : "") : "") : ""}
					creator_id={
						(propertyData.property as LocalPropertyInterface).created_by
							? (propertyData.property as LocalPropertyInterface).created_by.is_business_profile
								? ((propertyData.property as LocalPropertyInterface).created_by.user_id as ObjectId)
								: ((propertyData.property as LocalPropertyInterface).created_by.user_id as UserInterface)._id
							: ""
					}
					dispatch={dispatch}
					profile_context={profile_context}
					property_id={propertyData.property._id}
					property_created_by={(propertyData.property as LocalPropertyInterface).created_by}
					screenType={screenType}
				/>
			) : null}

			{/* Property Reviews */}

			{/* <InView
        as="div"
        threshold={0.5}
        // root={root}
        onChange={(inView, entry) => {
          if (inView === true && entry.isIntersecting === true) {
            setValue(6);
          }
        }}
        style={{ width: "100%" }}
      > */}
			{/* {propertyData.property &&
			propertyData.property.published &&
			profile_context &&
			profile_context.is_business_profile ? (
				<div
					ref={reviewsInnerRef}
					style={{ width: "100%" }}
				>
					<PropertyReviews
						property_id={propertyData.property._id}
						property_slug={propertyData.property.slug}
						location_title={propertyData.property.location.title}
						location_coordinates={propertyData.property.search_metrics.location_coordinates}
						reviews={propertyData.propertyReviews}
						session={session}
						dispatch={dispatch}
						profile_context={profile_context}
						property_created_by={(propertyData.property as LocalPropertyInterface).created_by}
						negativeScroll={negativeScroll}
						loading={loading_reviews}
					/>
				</div>
			) : null} */}
			{/* {is_user_creator ||

			{/* </InView> */}
			{/* Neighbourhood Reviews */}
			{/* {propertyData.property.search_metrics.location_coordinates[0] === 0 &&
			propertyData.property.search_metrics.location_coordinates[1] === 0 ? null : (
				<div
					ref={neighbourhoodReviewsInnerRef}
					style={{ width: "100%" }}
				>
					<NeighbourHoodReviews
						property_id={propertyData.property._id}
						location_title={propertyData.property.location.title}
						location_coordinates={propertyData.property.search_metrics.location_coordinates}
						reviews={propertyData.neighbourhoodReviews}
						session={session}
						dispatch={dispatch}
						profile_context={profile_context}
						property_created_by={(propertyData.property as LocalPropertyInterface).created_by}
						loading={loading_neighbourhood_reviews}
						property_slug={propertyData.property.slug}
					/>
				</div>
			)} */}
		</Container>
	);
};

export default RightContainer;
